import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, getToken } from '../lib/api';
import { can, useAuth, ROLE } from '../lib/auth';
import {
  inr, parseSteps, rupees, unitLabel, workRate, type PastingOperation, type RatesResponse,
} from '../lib/pasting';
import { ContractorBill, PastingRatesEditor } from './PastingRates';
import { ContractorBills } from './ContractorBills';

interface ProdRow {
  ProductionLogID: number;
  Job_ID: string;
  Job_Name: string | null;
  Client_Name: string | null;
  ProductionDate: string;
  MachineName: string | null;
  Operator: string | null;
  Contractor: string | null;
  JobType: string;
  JobTypeOther: string | null;
  Quantity: number;
  Remarks: string | null;
  CreatedOn: string;
  CreatedByName: string | null;
  Operation_ID?: number | null;
  OperationName?: string | null;
  OperationKind?: string | null;
  Pasted?: boolean | null;
  Steps?: string | null;
  /** How the entry differs from the whole job: "without Sorting", "pasting only"… */
  Variant?: string;
  Unit?: string | null;
  Hours?: number | null;
  CountsAsProduced?: boolean | number;
  // Super Admin only.
  Rate?: number | null;
  Amount?: number | null;
  /** QC_PENDING | QC_APPROVED | QC_REJECTED | QC_REWORK; NULL = before QC. */
  QCStatus?: string | null;
  /** On a finalized contractor bill — changes only through /edit-locked. */
  IsLocked?: boolean | number | null;
  Locked_Bill_ID?: number | null;
  /** Checked by VPPL / Super Admin; null = still to verify. */
  VerifiedOn?: string | null;
  VerifiedByName?: string | null;
  /** QC sent this many cartons back to be sorted again (QC_REWORK), and why. */
  ReworkQty?: number | null;
  QCReason?: string | null;
}

interface CorrectResult {
  ok: true;
  changed: string[];
  billId: number | null;
  /** Live work total − the finalized bill's work total; null for whoever cannot see bills. */
  billDrift: number | null;
}

interface AuditRow {
  id: string;
  code: string;
  field: string | null;
  old: string | null;
  new: string | null;
  billId: number | null;
  reason: string | null;
  at: string;
  by: string | null;
}

/** What the list's filters add up to, over every page. */
interface ProdSummary {
  entries: number;
  /** Cartons that count towards their jobs. */
  produced: number;
  byBox: { box: string; entries: number; qty: number; produced: number; hours: number }[];
  byProduct: { jobId: string; product: string | null; party: string | null; box: string; entries: number; qty: number; produced: number }[];
}

/** An entry edited in its own row. */
interface InlineEdit {
  id: number;
  date: string;
  machine: string;
  operator: string;
  contractor: string;
  operationId: string;
  /** Cartons, or hours for per-hour work. */
  qty: string;
  remarks: string;
  /** Box types: the pasting was done. */
  pasted: boolean;
  /** Box types: the steps not done (unticked). */
  skipped: number[];
}

const isLocked = (r: ProdRow) => Number(r.IsLocked) === 1;

/** The chip a row shows: pending billed-only work at Quality is resorted work back for its re-check. */
const qcChipKey = (r: ProdRow) =>
  r.QCStatus === 'QC_PENDING' && r.CountsAsProduced != null && !Number(r.CountsAsProduced) ? 'QC_RECHECK' : (r.QCStatus ?? '');

const monthName = (ym: string) =>
  new Date(`${ym}-15T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** First and last day of the entry's month — a locked entry cannot leave it. */
function monthBounds(day: string): { min: string; max: string } {
  const [y, m] = day.slice(0, 7).split('-').map(Number);
  const last = new Date(Date.UTC(y ?? 2000, m ?? 1, 0)).getUTCDate();
  return { min: `${day.slice(0, 7)}-01`, max: `${day.slice(0, 7)}-${String(last).padStart(2, '0')}` };
}

const QC_CHIP: Record<string, { text: string; cls: string }> = {
  QC_PENDING: { text: 'QC pending', cls: 'bg-slate-100 text-slate-600' },
  QC_APPROVED: { text: 'QC passed', cls: 'bg-emerald-100 text-emerald-800' },
  QC_REJECTED: { text: 'QC rejected', cls: 'bg-red-100 text-red-700' },
  QC_REWORK: { text: 'For resorting', cls: 'bg-orange-100 text-orange-800' },
  /** Resorted cartons back at Quality (billed-only work waiting for its check). */
  QC_RECHECK: { text: 'Re-check at Quality', cls: 'bg-sky-100 text-sky-800' },
};

interface JobLookup {
  Job_ID: string;
  Job_Name: string | null;
  Total_Boxes: number | null;
  PO_Number: string | null;
  Client_Name: string | null;
  /** Box type of the job's last entry. */
  LastBoxOperation_ID?: number | null;
  /** Job_Staus — 'Closed' takes an entry only after "add anyway?". */
  JobStatus?: string | null;
}

function friendlyProdError(msg: string): string {
  if (msg === 'job_is_closed') return 'This job card is closed — not saved.';
  if (msg === 'quantity_must_be_positive') return 'Quantity must be a positive whole number.';
  if (msg === 'hours_must_be_positive') return 'Enter the hours (more than 0).';
  if (msg === 'operation_inactive') return 'That operation is switched off — pick another.';
  if (msg === 'operation_not_found') return 'That operation no longer exists — pick another.';
  if (msg === 'nothing_done') return 'Tick at least one step — or Pasting.';
  if (msg === 'step_not_found') return 'A ticked step no longer exists — reload the page.';
  if (msg === 'pasting_migration_pending') return 'The server database needs its update (pasting rates) before entries can be saved.';
  if (msg === 'job_card_not_found') return 'Job card not found — check the ID.';
  if (msg === 'missing_fields') return 'Fill in the Job ID and the date.';
  if (msg === 'contractor_required') return 'Pick the contractor who waited.';
  if (msg === 'waiting_reason_required') return 'Write why the contractor had to wait.';
  if (msg === 'qc_inspected_reopen_first') return 'QC has already inspected this batch — its job, quantity and produced flag are fixed. Ask QC to reopen the inspection first.';
  if (msg === 'locked_record_cannot_move') return 'A locked entry stays with its contractor and month. To move it, Super Admin must unlock its bill first.';
  if (msg === 'reason_required') return 'Enter the reason for the correction (at least 3 letters).';
  if (msg === 'reason_too_long') return 'Keep the reason under 500 characters.';
  if (msg === 'nothing_changed') return 'Nothing was changed.';
  if (msg === 'record_not_locked') return 'This entry is not locked any more — reload the page and edit it normally.';
  if (msg === 'contractor_inactive') return 'That contractor is marked not working — pick an active contractor.';
  if (msg === 'contractor_deleted' || msg === 'contractor_not_found') return 'That contractor is not in the Contractor Master — pick one from the list.';
  if (msg === 'invalid_date') return 'Pick a valid date.';
  if (msg === 'not_found') return 'This entry no longer exists — reload the page.';
  if (msg === 'forbidden' || msg === 'super_admin_only') return 'You do not have permission for this.';
  if (msg === 'verified_on_finalized_bill') return 'This entry is on a finalized contractor bill — its Verified mark stays.';
  if (msg === 'verify_migration_pending') return 'The server database needs its update (production verify) before entries can be verified.';
  if (msg === 'billing_migration_pending') return 'The server database needs its update (contractor billing) first.';
  const lk = /^record_locked:(\d*)$/.exec(msg);
  if (lk) return `This entry is on finalized bill${lk[1] ? ` #${lk[1]}` : ''} and is locked. Use Correct (Super Admin / VPPL) with a reason.`;
  const mf = /^billing_month_finalized:(\d{4}-\d{2})$/.exec(msg);
  if (mf?.[1]) return `This contractor's bill for ${monthName(mf[1])} is finalized — entries cannot be added to it or moved into it. Super Admin can unlock the bill if this is needed.`;
  const exc = /^production_exceeds_order:(\d+):(\d+)$/.exec(msg);
  if (exc) return `This entry would exceed 150% of the order qty (${Number(exc[1]).toLocaleString()}). Already produced: ${Number(exc[2]).toLocaleString()}. Check the quantity.`;
  const uf = /^delete_would_underflow:(\d+):(\d+)$/.exec(msg);
  if (uf) return `Cannot delete — ${Number(uf[1]).toLocaleString()} already dispatched but only ${Number(uf[2]).toLocaleString()} would remain produced.`;
  return msg;
}

const today = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
};
/** The 1st of this month - the list opens on this month so far. */
const monthStart = () => `${today().slice(0, 8)}01`;

const fmtDate = (s: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const emptyForm = () => ({
  jobId: '',
  productionDate: today(),
  machineName: '',
  operator: '',
  contractor: '',
  operationId: '',
  quantity: '',
  hours: '',
  remarks: '',
  /** Box types: the pasting was done. */
  pasted: true,
  /** Box types: the steps the contractor did not do (unticked). */
  skipped: [] as number[],
});

type EntryForm = ReturnType<typeof emptyForm>;
type TextField = Exclude<keyof EntryForm, 'pasted' | 'skipped'>;

type Tab = 'entries' | 'bill' | 'rates';

export function ProductionEntryPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;
  // Bills: Super Admin and VPPL.  The rate card stays Super Admin's.
  // Everyone on Production Entry may look at the bills; only MANAGE_BILLING moves money.
  const canBill = can(user, 'MANAGE_BILLING') || can(user, 'VIEW_BILLING');
  const [tab, setTab] = useState<Tab>('entries');
  const tabs: [Tab, string][] = [
    ['entries', 'Entries'],
    ...(canBill ? [['bill', 'Contractor Bills'] as [Tab, string]] : []),
    ...(isSuperAdmin ? [['rates', 'Pasting Rates'] as [Tab, string]] : []),
  ];

  return (
    <div className="p-6 space-y-6">
      {tabs.length > 1 && (
        <div className="flex gap-4 border-b border-slate-200">
          {tabs.map(([k, label]) => (
            <button
              key={k}
              className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 ${tab === k ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => setTab(k)}
            >{label}</button>
          ))}
        </div>
      )}
      {tab === 'entries' && (
        <ProductionEntries isSuperAdmin={isSuperAdmin} canEdit={isSuperAdmin || user?.rollId === ROLE.VPPL}
          canCorrect={can(user, 'CORRECT_LOCKED_RECORDS')}
          canAdd={can(user, 'ADD_PRODUCTION_ENTRY')} canVerify={can(user, 'VERIFY_PRODUCTION')} />
      )}
      {/* Before the billing migration Super Admin keeps the live bill. */}
      {tab === 'bill' && canBill && <ContractorBills legacy={isSuperAdmin ? <ContractorBill /> : undefined} />}
      {tab === 'rates' && isSuperAdmin && <PastingRatesEditor />}
    </div>
  );
}

function ProductionEntries({ isSuperAdmin, canEdit, canCorrect, canAdd, canVerify }: {
  isSuperAdmin: boolean;
  /** Super Admin and VPPL edit and delete entries. */
  canEdit: boolean;
  canCorrect: boolean;
  /** Dispatch add entries; VPPL and Super Admin may too. */
  canAdd: boolean;
  /** VPPL and Super Admin verify them. */
  canVerify: boolean;
}) {
  const qc = useQueryClient();

  const [form, setForm] = useState(emptyForm);
  const [jobInfo, setJobInfo] = useState<JobLookup | null>(null);
  const [lookupErr, setLookupErr] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  /** Correcting a billing-locked entry: the bill it is on, and the month it cannot leave. */
  const [correcting, setCorrecting] = useState<{ billId: number | null; day: string } | null>(null);
  const [reason, setReason] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [auditId, setAuditId] = useState<number | null>(null);

  // No date range until one is picked and Filter pressed: every entry, newest first.
  const [filterFrom, setFilterFrom] = useState(monthStart);
  const [filterTo, setFilterTo] = useState(today);
  const [appliedFrom, setAppliedFrom] = useState(monthStart);
  const [appliedTo, setAppliedTo] = useState(today);
  const [page, setPage] = useState(1);
  /** '' = every entry; verify = still to verify; resorting = Quality sent back; recheck = resorted, back at Quality. */
  const [stateFilter, setStateFilter] = useState<'' | 'verify' | 'resorting' | 'recheck'>('');
  const [contractorFilter, setContractorFilter] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  /** The box type whose products are opened in the summary ('' = none). */
  const [openBox, setOpenBox] = useState('');

  // Only the Pasting department's machines (Machine master → Department).
  const machines = useQuery({
    queryKey: ['lookup-machines', 'pasting'],
    queryFn: () => api<{ id: string; name: string }[]>('/api/lookups/machines?department=Pasting'),
    staleTime: 5 * 60_000,
  });

  const operations = useQuery({
    queryKey: ['pasting-operations'],
    queryFn: () => api<{ rows: PastingOperation[] }>('/api/pasting/operations'),
    staleTime: 5 * 60_000,
  });
  const allOps = operations.data?.rows ?? [];
  const activeOps = allOps.filter((o) => o.isActive);
  const selectedOp = allOps.find((o) => String(o.id) === form.operationId);
  const perHour = selectedOp?.unit === 'PER_HOUR';

  // A box type is priced for the whole job; the ticks say which steps were done.
  const isBox = selectedOp?.kind === 'BOX';
  const stepOps = activeOps.filter((o) => o.kind === 'STEP');
  const doneSteps = stepOps.filter((o) => !form.skipped.includes(o.id));
  const skippedSteps = stepOps.filter((o) => form.skipped.includes(o.id));
  const nothingDone = isBox && !form.pasted && doneSteps.length === 0;
  // Adds to the job's produced qty — a box type only when it was pasted.
  const producing = !selectedOp || (selectedOp.countsAsProduced && (!isBox || form.pasted));

  // The rate hint under Box Type — money, so Super Admin only.
  const rates = useQuery({
    enabled: isSuperAdmin,
    queryKey: ['pasting-rates'],
    queryFn: () => api<RatesResponse>('/api/pasting/rates'),
    staleTime: 60_000,
  });
  const cardToday = rates.data?.cards.find((c) => c.id === rates.data?.currentCardId);
  const liveRate = isSuperAdmin ? workRate(cardToday, selectedOp, stepOps, form.skipped, form.pasted) : null;

  const contractors = useQuery({
    queryKey: ['production-contractors'],
    queryFn: () => api<{ rows: string[] }>('/api/production/contractors'),
    staleTime: 5 * 60_000,
  });

  const list = useQuery({
    queryKey: ['production', appliedFrom, appliedTo, page, stateFilter, contractorFilter, appliedSearch],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (appliedFrom) sp.set('from', appliedFrom);
      if (appliedTo) sp.set('to', appliedTo);
      sp.set('page', String(page));
      sp.set('limit', '50');
      if (stateFilter === 'verify') sp.set('verified', '0');
      if (stateFilter === 'resorting' || stateFilter === 'recheck') sp.set('qc', stateFilter);
      if (contractorFilter) sp.set('contractor', contractorFilter);
      if (appliedSearch) sp.set('q', appliedSearch);
      return api<{ total: number; page: number; limit: number; rows: ProdRow[]; verifyReady?: boolean; summary?: ProdSummary }>(`/api/production?${sp}`);
    },
    placeholderData: (prev) => prev,
  });

  const saveMut = useMutation({
    mutationFn: async (data: EntryForm & { id?: number }) => {
      const op = allOps.find((o) => String(o.id) === data.operationId);
      const payload = {
        jobId: jobInfo?.Job_ID ?? data.jobId,
        productionDate: data.productionDate,
        machineName: data.machineName,
        operator: data.operator,
        contractor: data.contractor,
        operationId: Number(data.operationId),
        ...(op?.kind === 'BOX' ? {
          pasted: data.pasted,
          steps: stepOps.filter((o) => !data.skipped.includes(o.id)).map((o) => o.id),
        } : {}),
        ...(op?.unit === 'PER_HOUR' ? { hours: Number(data.hours) } : { quantity: Number(data.quantity) }),
        remarks: data.remarks,
      };
      const send = (extra: { allowClosedJob?: boolean } = {}) => {
        if (data.id && correcting) {
          return api<CorrectResult>(`/api/production/${data.id}/edit-locked`, {
            method: 'PUT', body: JSON.stringify({ ...payload, ...extra, reason: reason.trim() }),
          });
        }
        if (data.id) return api(`/api/production/${data.id}`, { method: 'PUT', body: JSON.stringify({ ...payload, ...extra }) });
        return api('/api/production', { method: 'POST', body: JSON.stringify({ ...payload, ...extra }) });
      };
      try {
        return await send();
      } catch (e) {
        // A closed job card takes the entry only when the user says so.
        if (e instanceof ApiError && e.message === 'job_is_closed'
            && window.confirm(`Job card ${payload.jobId} is closed.\n\n${data.id ? 'Save this entry anyway?' : 'Add this entry anyway?'}`)) {
          return send({ allowClosedJob: true });
        }
        throw e;
      }
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['production'] });
      qc.invalidateQueries({ queryKey: ['production-contractors'] });
      qc.invalidateQueries({ queryKey: ['pasting-bill'] });
      qc.invalidateQueries({ queryKey: ['billing'] });
      qc.invalidateQueries({ queryKey: ['billing-bill'] });
      if (correcting) {
        const r = res as CorrectResult;
        const bill = r.billId != null ? `bill #${r.billId}` : 'the bill';
        const drift = r.billDrift == null ? null : Number(r.billDrift) || 0;
        setSavedNote(`Corrected ${r.changed.join(', ')} — logged with your reason. ${drift == null
          ? `Super Admin sees the change on ${bill}.`
          : drift === 0
            ? `The total of ${bill} is unchanged.`
            : `The work now comes to ${rupees(Math.abs(drift))} ${drift > 0 ? 'more' : 'less'} than ${bill}; if the difference should be ${drift > 0 ? 'paid' : 'recovered'}, add an adjustment on Contractor Bills.`}`);
      } else {
        setSavedNote(editId ? 'Updated!' : 'Saved!');
      }
      setForm(emptyForm());
      setJobInfo(null);
      setEditId(null);
      setCorrecting(null);
      setReason('');
    },
  });

  /** The entry being edited in its own row (Super Admin), and what it holds now. */
  const [inline, setInline] = useState<InlineEdit | null>(null);
  const [inlineErr, setInlineErr] = useState('');
  const inlineMut = useMutation({
    mutationFn: async ({ row, ed }: { row: ProdRow; ed: InlineEdit }) => {
      const op = allOps.find((o) => String(o.id) === ed.operationId);
      const payload = {
        jobId: row.Job_ID,
        productionDate: ed.date,
        machineName: ed.machine,
        operator: ed.operator,
        contractor: ed.contractor,
        operationId: Number(ed.operationId),
        // Box types: the work done, as ticked in the row.
        ...(op?.kind === 'BOX' ? {
          pasted: ed.pasted,
          steps: stepOps.filter((o) => !ed.skipped.includes(o.id)).map((o) => o.id),
        } : {}),
        ...(op?.unit === 'PER_HOUR' ? { hours: Number(ed.qty) } : { quantity: Number(ed.qty) }),
        remarks: ed.remarks,
      };
      const send = (extra: { allowClosedJob?: boolean } = {}) =>
        api(`/api/production/${row.ProductionLogID}`, { method: 'PUT', body: JSON.stringify({ ...payload, ...extra }) });
      try {
        return await send();
      } catch (e) {
        if (e instanceof ApiError && e.message === 'job_is_closed'
            && window.confirm(`Job card ${row.Job_ID} is closed.\n\nSave this entry anyway?`)) {
          return send({ allowClosedJob: true });
        }
        throw e;
      }
    },
    onSuccess: () => {
      setInline(null);
      setInlineErr('');
      qc.invalidateQueries({ queryKey: ['production'] });
      qc.invalidateQueries({ queryKey: ['pasting-bill'] });
      qc.invalidateQueries({ queryKey: ['billing'] });
      qc.invalidateQueries({ queryKey: ['billing-bill'] });
    },
    onError: (e: Error) => setInlineErr(friendlyProdError(e.message)),
  });
  function startInline(row: ProdRow) {
    setInlineErr('');
    inlineMut.reset();
    setInline({
      id: row.ProductionLogID,
      date: row.ProductionDate?.slice(0, 10) ?? today(),
      machine: row.MachineName ?? '',
      operator: row.Operator ?? '',
      contractor: row.Contractor ?? '',
      operationId: String(row.Operation_ID ?? allOps.find((o) => o.legacyJobType === row.JobType)?.id ?? ''),
      qty: row.Unit === 'PER_HOUR' ? String(row.Hours ?? '') : String(row.Quantity ?? ''),
      remarks: row.Remarks ?? '',
      ...workOf(row),
    });
  }
  /** The work done on an entry, as the ticks show it: pasted, and the steps not done. */
  function workOf(row: ProdRow): { pasted: boolean; skipped: number[] } {
    const marks = parseSteps(row.Steps);
    const pasted = row.Pasted == null ? true : Boolean(row.Pasted);
    return {
      pasted,
      // Pasted: the steps marked not done.  Not pasted: every step not marked done.
      skipped: pasted
        ? marks.filter((m) => !m.done).map((m) => m.id)
        : stepOps.filter((o) => !marks.some((m) => m.id === o.id && m.done)).map((o) => o.id),
    };
  }
  const toggleEdStep = (id: number) =>
    setInline((x) => (x ? { ...x, skipped: x.skipped.includes(id) ? x.skipped.filter((y) => y !== id) : [...x.skipped, id] } : x));
  const setEd = (k: keyof Omit<InlineEdit, 'id'>) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setInline((x) => (x ? { ...x, [k]: e.target.value } : x));

  const deleteMut = useMutation({
    mutationFn: (id: number) => api(`/api/production/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production'] }),
    onError: (e: Error) => alert(friendlyProdError(e.message)),
  });

  const verifyMut = useMutation({
    mutationFn: (v: { ids: number[]; verified: boolean }) =>
      api<{ ok: true; changed: number }>('/api/production/verify', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production'] }),
    onError: (e: Error) => alert(friendlyProdError(e.message)),
  });

  async function lookupJob() {
    const jid = form.jobId.trim();
    if (!jid) { setJobInfo(null); setLookupErr(''); return; }
    try {
      setLookupErr('');
      const data = await api<JobLookup>(`/api/production/lookup/${encodeURIComponent(jid)}`);
      setJobInfo(data);
      // Start on the box type of the job's last entry, unless one is picked.
      const last = data.LastBoxOperation_ID;
      if (last != null && activeOps.some((o) => o.id === last && o.kind === 'BOX')) {
        setForm((f) => (f.operationId ? f : { ...f, operationId: String(last) }));
      }
    } catch {
      setJobInfo(null);
      setLookupErr('Job not found');
    }
  }

  function startEdit(row: ProdRow) {
    setCorrecting(null);
    setReason('');
    setSavedNote('');
    saveMut.reset();
    setEditId(row.ProductionLogID);
    const marks = parseSteps(row.Steps);
    const pasted = row.Pasted == null ? true : Boolean(row.Pasted);
    setForm({
      jobId: String(row.Job_ID),
      productionDate: row.ProductionDate?.slice(0, 10) ?? today(),
      machineName: row.MachineName ?? '',
      operator: row.Operator ?? '',
      contractor: row.Contractor ?? '',
      // Entries from before the rate card map through their old Job Type.
      operationId: String(row.Operation_ID ?? allOps.find((o) => o.legacyJobType === row.JobType)?.id ?? ''),
      quantity: String(row.Quantity),
      hours: row.Hours == null ? '' : String(row.Hours),
      remarks: row.Remarks ?? '',
      pasted,
      // Pasted: the steps marked not done.  Not pasted: every step not marked done.
      skipped: pasted
        ? marks.filter((m) => !m.done).map((m) => m.id)
        : stepOps.filter((o) => !marks.some((m) => m.id === o.id && m.done)).map((o) => o.id),
    });
    setJobInfo({ Job_ID: row.Job_ID, Job_Name: row.Job_Name, Total_Boxes: null, PO_Number: null, Client_Name: row.Client_Name });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Same form, for an entry a finalized bill has locked: a reason is required, contractor and month stay. */
  function startCorrect(row: ProdRow) {
    startEdit(row);
    setCorrecting({ billId: row.Locked_Bill_ID ?? null, day: row.ProductionDate?.slice(0, 10) ?? today() });
  }

  /**
   * The contractor has sorted a batch Quality sent back: open the form as a
   * Resorting entry for the same job, contractor and cartons.  Saved, it goes
   * to Quality for the re-check.
   */
  function startResorted(row: ProdRow) {
    const op = activeOps.find((o) => o.needsQc && o.kind !== 'BOX') ?? activeOps.find((o) => /resort/i.test(o.name));
    setEditId(null);
    setCorrecting(null);
    setReason('');
    setSavedNote('');
    saveMut.reset();
    setForm({
      ...emptyForm(),
      jobId: String(row.Job_ID),
      machineName: row.MachineName ?? '',
      operator: row.Operator ?? '',
      contractor: row.Contractor ?? '',
      operationId: op ? String(op.id) : '',
      quantity: row.ReworkQty ? String(row.ReworkQty) : '',
      remarks: `Resorted - sent back by Quality${row.QCReason ? `: ${row.QCReason}` : ''}`.slice(0, 500),
    });
    setJobInfo({ Job_ID: row.Job_ID, Job_Name: row.Job_Name, Total_Boxes: null, PO_Number: null, Client_Name: row.Client_Name });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** The contractor-wise daily report for the dates and contractor filtered, as Excel. */
  const [downloading, setDownloading] = useState(false);
  async function downloadReport() {
    const sp = new URLSearchParams();
    if (appliedFrom) sp.set('from', appliedFrom);
    if (appliedTo) sp.set('to', appliedTo);
    if (contractorFilter) sp.set('contractor', contractorFilter);
    setDownloading(true);
    try {
      const res = await fetch(`/api/production/contractor-report.xlsx?${sp}`, { headers: { authorization: `Bearer ${getToken() ?? ''}` } });
      if (!res.ok) throw new Error(String(res.status));
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'contractor-report.xlsx';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      alert('Could not prepare the report.');
    } finally {
      setDownloading(false);
    }
  }

  function cancelEdit() {
    setEditId(null);
    setCorrecting(null);
    setReason('');
    setForm(emptyForm());
    setJobInfo(null);
  }

  function applyFilter(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setAppliedFrom(filterFrom);
    setAppliedTo(filterTo);
    setAppliedSearch(search.trim());
  }

  const set = (k: TextField) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleStep = (id: number) =>
    setForm((f) => ({ ...f, skipped: f.skipped.includes(id) ? f.skipped.filter((x) => x !== id) : [...f.skipped, id] }));

  const workNote = nothingDone
    ? 'Tick at least one.'
    : !form.pasted
      ? "Not pasted — this batch does not add to the job's produced qty."
      : skippedSteps.length
        ? `Not done: ${skippedSteps.map((o) => o.name).join(', ')}.`
        : 'Whole job — every step done.';

  // Waiting hours: no job, machine or box — the contractor, the hours and why.
  const canSave = (perHour || form.jobId) && form.productionDate && form.operationId && !nothingDone
    && (perHour
      ? Number(form.hours) > 0 && form.contractor.trim() !== '' && form.remarks.trim() !== ''
      : Number(form.quantity) > 0)
    && (!correcting || reason.trim().length >= 3);

  /** Picking waiting hours on a new entry drops the job details it does not need. */
  function pickWork(id: string) {
    const op = allOps.find((o) => String(o.id) === id);
    const toWaiting = op?.unit === 'PER_HOUR' && !editId;
    setForm((f) => (toWaiting ? { ...f, operationId: id, jobId: '', machineName: '', operator: '' } : { ...f, operationId: id }));
    if (toWaiting) { setJobInfo(null); setLookupErr(''); }
  }

  const contractorNames = contractors.data?.rows ?? [];
  const bounds = correcting ? monthBounds(correcting.day) : null;
  const showActions = canEdit || canCorrect || canVerify;
  // The form is for whoever adds entries; an edit or a correction opens it too.
  const showForm = canAdd || editId != null;
  const verifyOn = list.data?.verifyReady === true;
  const pendingHere = (list.data?.rows ?? []).filter((r) => !r.VerifiedOn).map((r) => r.ProductionLogID);

  // Shared by the work layout and the waiting-hours layout.
  const workField = (
    <div>
      <label className="label">{perHour ? 'Work *' : 'Box Type *'}</label>
      <select className="input" value={form.operationId} onChange={(e) => pickWork(e.target.value)} required>
        <option value="">Select…</option>
        <optgroup label="Box type">
          {activeOps.filter((o) => o.kind === 'BOX').map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </optgroup>
        <optgroup label="Other work — billed to contractor only">
          {activeOps.filter((o) => o.kind === 'EXTRA').map((o) => (
            <option key={o.id} value={o.id}>{o.name}{o.unit === 'PER_HOUR' ? ' (hours)' : ''}</option>
          ))}
        </optgroup>
        {/* An old entry may sit on a switched-off operation, or on a step entered alone. */}
        {selectedOp && (!selectedOp.isActive || selectedOp.kind === 'STEP') && (
          <option value={selectedOp.id}>{selectedOp.name} ({selectedOp.isActive ? 'step on its own' : 'switched off'})</option>
        )}
      </select>
      {selectedOp && !isBox && !selectedOp.countsAsProduced && (
        <p className="text-[11px] text-amber-700 mt-0.5">
          {perHour
            ? 'Paid to the contractor by the hour — no job or machine needed.'
            : selectedOp.needsQc
              ? "Not added to the job's produced qty — goes to Quality for approval again."
              : "Billed to the contractor; not added to the job's produced qty."}
        </p>
      )}
      {liveRate != null && cardToday && !nothingDone && (
        <p className="text-[11px] text-slate-500 mt-0.5">
          Rate: {rupees(liveRate)} {unitLabel(selectedOp?.unit)}
        </p>
      )}
    </div>
  );
  const dateField = (
    <div>
      <label className="label">Date *</label>
      <input
        type="date"
        className="input"
        value={form.productionDate}
        onChange={set('productionDate')}
        min={bounds?.min}
        max={bounds?.max}
        required
      />
    </div>
  );
  const totalPages = list.data ? Math.ceil(list.data.total / list.data.limit) : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-brand">
          {correcting
            ? `Correct Locked Entry${correcting.billId != null ? ` (Bill #${correcting.billId})` : ''}`
            : editId ? 'Edit Production Entry' : 'Production Entry'}
        </h1>
        <p className="text-xs text-slate-500">
          Log each batch after it goes through a machine. Replaces the Google Form.
          {verifyOn && ' An entry goes on the contractor bill once VPPL or Super Admin has verified it.'}
        </p>
      </div>

      {!showForm && (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Entries are added by Dispatch.{canVerify ? ' Check each one below and mark it Verified — only verified entries go on the contractor bill.' : ''}
        </div>
      )}

      {/* ── Entry form ── */}
      {showForm && <form
        className={`card p-4 space-y-3 ${correcting ? 'border-amber-300 ring-1 ring-amber-200' : ''}`}
        onSubmit={(e) => {
          e.preventDefault();
          setSavedNote('');
          saveMut.mutate(editId ? { ...form, id: editId } : form);
        }}
      >
        {correcting && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This entry is on a finalized contractor bill. Every field you change is logged with the old value, the new
            value, your name and the reason. The contractor and the month cannot change here, and the bill's total does
            not change by itself: {isSuperAdmin
              ? 'add an adjustment on Contractor Bills if the difference should be paid.'
              : 'Super Admin settles any difference on the bill.'}
          </div>
        )}
        {perHour ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {workField}
              {dateField}
              <div>
                <label className="label">Contractor *</label>
                <select
                  className={`input ${correcting ? 'bg-slate-100' : ''}`}
                  value={form.contractor}
                  onChange={set('contractor')}
                  disabled={!!correcting}
                  required
                >
                  <option value="">Select…</option>
                  {contractorNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  {/* An old entry's contractor may since have been marked not working. */}
                  {form.contractor && !contractorNames.includes(form.contractor) && (
                    <option value={form.contractor}>{form.contractor}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="label">Hours *</label>
                <input type="number" className="input" value={form.hours} onChange={set('hours')} min={0.25} step={0.25} required placeholder="e.g. 2.5" />
              </div>
            </div>
            <div>
              <label className="label">Reason for waiting *</label>
              <input
                type="text"
                className="input"
                value={form.remarks}
                onChange={set('remarks')}
                maxLength={500}
                required
                placeholder="e.g. no board on the floor, machine breakdown, waiting for approval"
              />
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Job ID + auto-fill */}
              <div>
                <label className="label">Job ID *</label>
                <input
                  type="text"
                  className="input"
                  value={form.jobId}
                  onChange={set('jobId')}
                  onBlur={lookupJob}
                  placeholder="e.g. 81023 or VP/082026/81023"
                  required
                />
                {lookupErr && <p className="text-xs text-red-500 mt-0.5">{lookupErr}</p>}
                {jobInfo?.JobStatus === 'Closed' && (
                  <p className="text-xs text-amber-700 mt-0.5">This job card is closed — you'll be asked before it is saved.</p>
                )}
              </div>
              {/* Auto-filled info */}
              <div>
                <label className="label">Product</label>
                <input type="text" className="input bg-slate-100" readOnly value={jobInfo?.Job_Name ?? ''} tabIndex={-1} />
              </div>
              <div>
                <label className="label">Party</label>
                <input type="text" className="input bg-slate-100" readOnly value={jobInfo?.Client_Name ?? ''} tabIndex={-1} />
              </div>
              {dateField}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                {/* Billed-only work (Resorting, Waiting, not pasted…) need not be on a machine. */}
                <label className="label">Machine{producing ? ' *' : ''}</label>
                <select className="input" value={form.machineName} onChange={set('machineName')} required={producing}>
                  <option value="">{machines.data && !machines.data.length ? 'No Pasting machines — add them in Machine master' : 'Select…'}</option>
                  {(machines.data ?? []).map((m) => (
                    <option key={m.id} value={m.name}>{m.name}</option>
                  ))}
                  {/* An older entry on a machine from another department keeps it. */}
                  {form.machineName && machines.data && !machines.data.some((m) => m.name === form.machineName) && (
                    <option value={form.machineName}>{form.machineName}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="label">Operator</label>
                <input type="text" className="input" value={form.operator} onChange={set('operator')} placeholder="Operator name" />
              </div>
              <div>
                <label className="label">Contractor</label>
                {/* Picked from the Contractor Master, so the bill never splits on a spelling. */}
                <select
                  className={`input ${correcting ? 'bg-slate-100' : ''}`}
                  value={form.contractor}
                  onChange={set('contractor')}
                  disabled={!!correcting}
                  title={correcting ? 'A locked entry stays with its contractor' : undefined}
                >
                  <option value="">{contractors.isLoading ? 'Loading…' : contractorNames.length ? 'Select…' : 'No contractors — add them in Contractor Master'}</option>
                  {contractorNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  {/* An old entry's contractor may since have been marked not working. */}
                  {form.contractor && !contractorNames.includes(form.contractor) && (
                    <option value={form.contractor}>{form.contractor}</option>
                  )}
                </select>
              </div>
              {workField}
            </div>

            {isBox && (
              <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Work done</span>
                  {stepOps.map((o) => (
                    <label key={o.id} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!form.skipped.includes(o.id)} onChange={() => toggleStep(o.id)} />
                      {o.name}
                    </label>
                  ))}
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={form.pasted} onChange={(e) => setForm((f) => ({ ...f, pasted: e.target.checked }))} />
                    Pasting
                  </label>
                </div>
                <p className={`text-[11px] mt-1 ${nothingDone ? 'text-red-600' : form.pasted ? 'text-slate-500' : 'text-amber-700'}`}>{workNote}</p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="label">Quantity *</label>
                <input type="number" className="input" value={form.quantity} onChange={set('quantity')} min={1} required placeholder="e.g. 15000" />
              </div>
              <div className="md:col-span-2">
                <label className="label">Remarks</label>
                <input type="text" className="input" value={form.remarks} onChange={set('remarks')} placeholder="Optional" />
              </div>
            </div>
          </>
        )}

        {correcting && (
          <div>
            <label className="label">Reason for the correction *</label>
            <input
              type="text"
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Quantity entered as 15000, slip says 1500"
              required
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <button type="submit" className="btn-primary" disabled={!canSave || saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : correcting ? 'Save Correction' : editId ? 'Update Entry' : 'Add Entry'}
          </button>
          {editId && (
            <button type="button" className="btn-ghost text-xs" onClick={cancelEdit}>Cancel {correcting ? 'Correction' : 'Edit'}</button>
          )}
          {saveMut.isError && <span className="text-xs text-red-500">{friendlyProdError((saveMut.error as Error).message)}</span>}
          {saveMut.isSuccess && !editId && savedNote && <span className="text-xs text-emerald-600">{savedNote}</span>}
        </div>
      </form>}

      {/* ── Filter + list ── */}
      <form className="flex flex-wrap items-end gap-2" onSubmit={applyFilter}>
        <div>
          <label className="label">From</label>
          <input type="date" className="input" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[220px] max-w-md">
          <label className="label">Search</label>
          <input type="search" className="input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Job ID, product, party, machine, operator, contractor, box type…" />
        </div>
        <button className="btn-primary" type="submit">Filter</button>
        <button type="button" className="btn-ghost text-xs" onClick={() => { setFilterFrom(today()); setFilterTo(today()); setAppliedFrom(today()); setAppliedTo(today()); setAppliedSearch(search.trim()); setPage(1); }}>Today</button>
        <button type="button" className="btn-ghost text-xs" onClick={() => { setFilterFrom(monthStart()); setFilterTo(today()); setAppliedFrom(monthStart()); setAppliedTo(today()); setAppliedSearch(search.trim()); setPage(1); }}>This month</button>
        <button type="button" className="btn-ghost text-xs" onClick={() => { setFilterFrom(''); setFilterTo(''); setAppliedFrom(''); setAppliedTo(''); setAppliedSearch(search.trim()); setPage(1); }}>All</button>
        <div>
          <label className="label">Contractor</label>
          <select className="input" value={contractorFilter} onChange={(e) => { setContractorFilter(e.target.value); setPage(1); }}>
            <option value="">All contractors</option>
            {contractorNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Show</label>
          <select className="input" value={stateFilter} onChange={(e) => {
            const v = e.target.value as typeof stateFilter;
            setStateFilter(v); setPage(1);
            // To verify / resorting are to-do lists: every one pending, whatever its date.
            // Back to all entries: this month again.
            const from = v ? '' : monthStart(), to = v ? '' : today();
            setFilterFrom(from); setFilterTo(to); setAppliedFrom(from); setAppliedTo(to);
          }}>
            <option value="">All entries</option>
            {verifyOn && <option value="verify">To verify</option>}
            <option value="resorting">Sent for resorting by Quality</option>
            <option value="recheck">Resorted — back at Quality</option>
          </select>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs text-emerald-700 mb-0.5"
          disabled={downloading}
          title={appliedFrom || appliedTo ? 'Contractor-wise, day by day, for the dates filtered' : 'Contractor-wise, day by day - pick dates and Filter, or it is this month so far'}
          onClick={() => void downloadReport()}
        >
          {downloading ? 'Preparing…' : '⬇ Contractor report (Excel)'}
        </button>
        {verifyOn && canVerify && pendingHere.length > 1 && (
          <button
            type="button"
            className="btn-ghost text-xs text-emerald-700 mb-0.5"
            disabled={verifyMut.isPending}
            onClick={() => { if (confirm(`Mark the ${pendingHere.length} unverified entries on this page as verified?`)) verifyMut.mutate({ ids: pendingHere, verified: true }); }}
          >
            Verify all {pendingHere.length} shown
          </button>
        )}
      </form>

      {list.isLoading && <p className="text-slate-500">Loading…</p>}
      {list.isError && <p className="text-red-600">Failed: {(list.error as Error).message}</p>}

      {list.data && (
        <>
<p className="text-xs text-slate-500">{list.data.total} entries{appliedSearch && <> matching “{appliedSearch}”</>}</p>

          {/* What the filters add up to, over every page - not just the 50 shown. */}
          {list.data.summary && list.data.summary.byBox.length > 0 && (
            <div className="card p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h2 className="text-sm font-semibold text-brand">By box type</h2>
                <div className="text-xs text-slate-500">
                  Total produced <b className="text-base text-emerald-700">{list.data.summary.produced.toLocaleString('en-IN')}</b> cartons
                  {' '}· {list.data.summary.entries.toLocaleString('en-IN')} entries
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5">Box type / work</th>
                      <th className="px-2 py-1.5 text-right">Products</th>
                      <th className="px-2 py-1.5 text-right">Entries</th>
                      <th className="px-2 py-1.5 text-right">Quantity</th>
                      <th className="px-2 py-1.5 text-right">Produced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.summary.byBox.map((b) => {
                      const products = list.data!.summary!.byProduct.filter((p) => p.box === b.box);
                      const open = openBox === b.box;
                      return (
                        <FragmentRows key={b.box}>
                          <tr className="border-t border-slate-100 cursor-pointer hover:bg-yellow-50" onClick={() => setOpenBox(open ? '' : b.box)}
                            title="Show the products under this box type">
                            <td className="px-2 py-1.5 font-semibold"><span className="inline-block w-3 text-slate-400">{open ? '▾' : '▸'}</span>{b.box}</td>
                            <td className="px-2 py-1.5 text-right">{products.length}</td>
                            <td className="px-2 py-1.5 text-right">{b.entries.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1.5 text-right">{b.hours > 0 && !b.qty ? `${inr(b.hours, 2)} h` : b.qty.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1.5 text-right font-bold text-emerald-700">{b.produced.toLocaleString('en-IN')}</td>
                          </tr>
                          {open && products.map((p) => (
                            <tr key={`${p.jobId}|${p.box}`} className="border-t border-slate-50 bg-slate-50/60">
                              <td className="px-2 py-1 pl-7" colSpan={2}>
                                <span className="font-mono text-[11px] text-brand">{p.jobId || '—'}</span>
                                <span className="ml-2">{p.product ?? ''}</span>
                                {p.party && <span className="ml-2 text-slate-400">{p.party}</span>}
                              </td>
                              <td className="px-2 py-1 text-right">{p.entries}</td>
                              <td className="px-2 py-1 text-right">{p.qty.toLocaleString('en-IN')}</td>
                              <td className="px-2 py-1 text-right font-semibold text-emerald-700">{p.produced.toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </FragmentRows>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                    <tr>
                      <td className="px-2 py-1.5" colSpan={2}>Total</td>
                      <td className="px-2 py-1.5 text-right">{list.data.summary.entries.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right">{list.data.summary.byBox.reduce((s, b) => s + b.qty, 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-700">{list.data.summary.produced.toLocaleString('en-IN')}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Click a box type to see its products. Totals are of everything the dates and search match, not only this page.</p>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Job ID</th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Party</th>
                  <th className="px-3 py-2">Machine</th>
                  <th className="px-3 py-2">Operator</th>
                  <th className="px-3 py-2">Contractor</th>
                  <th className="px-3 py-2">Box / Work</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2">Status</th>
                  {isSuperAdmin && <th className="px-3 py-2 text-right">Rate</th>}
                  {isSuperAdmin && <th className="px-3 py-2 text-right">Amount</th>}
                  <th className="px-3 py-2">Remarks</th>
                  <th className="px-3 py-2">Entered By</th>
                  {showActions && <th className="px-3 py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {list.data.rows.map((r) => {
                  // This row is being edited in place.
                  const ed = inline?.id === r.ProductionLogID ? inline : null;
                  const edOp = ed ? allOps.find((o) => String(o.id) === ed.operationId) : undefined;
                  const cell = 'input py-1 px-1.5 text-xs min-w-[90px]';
                  return (
                  <tr key={r.ProductionLogID} className={`border-t border-slate-100 ${ed ? 'bg-yellow-50 align-top' : 'hover:bg-slate-50'}`}>
                    <td className="px-3 py-2 text-xs">
                      {ed ? <input type="date" className={cell} value={ed.date} onChange={setEd('date')} /> : fmtDate(r.ProductionDate)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.Job_ID || '—'}</td>
                    <td className="px-3 py-2 text-xs">{r.Job_Name}</td>
                    <td className="px-3 py-2 text-xs">{r.Client_Name}</td>
                    <td className="px-3 py-2 text-xs">
                      {ed ? (
                        <select className={cell} value={ed.machine} onChange={setEd('machine')}>
                          <option value="">—</option>
                          {(machines.data ?? []).map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                          {ed.machine && !(machines.data ?? []).some((m) => m.name === ed.machine) && <option value={ed.machine}>{ed.machine}</option>}
                        </select>
                      ) : r.MachineName}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {ed ? <input className={cell} value={ed.operator} onChange={setEd('operator')} /> : r.Operator}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {ed ? (
                        <select className={cell} value={ed.contractor} onChange={setEd('contractor')}>
                          <option value="">—</option>
                          {contractorNames.map((n) => <option key={n} value={n}>{n}</option>)}
                          {ed.contractor && !contractorNames.includes(ed.contractor) && <option value={ed.contractor}>{ed.contractor}</option>}
                        </select>
                      ) : r.Contractor}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {ed ? (
                        <select className={cell} value={ed.operationId} onChange={setEd('operationId')}>
                          <option value="">Select…</option>
                          <optgroup label="Box type">
                            {activeOps.filter((o) => o.kind === 'BOX').map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </optgroup>
                          <optgroup label="Other work">
                            {activeOps.filter((o) => o.kind === 'EXTRA').map((o) => <option key={o.id} value={o.id}>{o.name}{o.unit === 'PER_HOUR' ? ' (hours)' : ''}</option>)}
                          </optgroup>
                          {edOp && (!edOp.isActive || edOp.kind === 'STEP') && <option value={edOp.id}>{edOp.name}</option>}
                        </select>
                      ) : null}
                      {ed && edOp?.kind === 'BOX' && (
                        <div className="mt-1.5 space-y-0.5 rounded border border-slate-200 bg-white px-1.5 py-1">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Work done</div>
                          {stepOps.map((o) => (
                            <label key={o.id} className="flex items-center gap-1 whitespace-nowrap cursor-pointer">
                              <input type="checkbox" checked={!ed.skipped.includes(o.id)} onChange={() => toggleEdStep(o.id)} />
                              {o.name}
                            </label>
                          ))}
                          <label className="flex items-center gap-1 whitespace-nowrap cursor-pointer">
                            <input type="checkbox" checked={ed.pasted}
                              onChange={(e) => setInline((x) => (x ? { ...x, pasted: e.target.checked } : x))} />
                            Pasting
                          </label>
                          {!ed.pasted && !stepOps.some((o) => !ed.skipped.includes(o.id)) && (
                            <div className="text-[10px] text-red-600">Tick at least one.</div>
                          )}
                        </div>
                      )}
                      {ed ? null : r.OperationName ?? (r.JobType === 'Other' ? r.JobTypeOther : r.JobType)}
                      {r.CountsAsProduced != null && !Number(r.CountsAsProduced) && (
                        <span className="ml-1 text-[10px] text-amber-700">billed only</span>
                      )}
                      {r.Variant && <span className="block text-[10px] text-slate-500">{r.Variant}</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {ed ? (
                        <input type="number" className={`${cell} w-24 text-right`} min={edOp?.unit === 'PER_HOUR' ? 0.25 : 1}
                          step={edOp?.unit === 'PER_HOUR' ? 0.25 : 1} value={ed.qty} onChange={setEd('qty')}
                          title={edOp?.unit === 'PER_HOUR' ? 'Hours' : 'Cartons'} />
                      ) : r.Unit === 'PER_HOUR' ? `${inr(Number(r.Hours ?? 0), 2)} h` : r.Quantity?.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-[10px] whitespace-nowrap space-y-0.5">
                      {isLocked(r) && (
                        <span className="block w-fit rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800" title="On a finalized contractor bill">
                          🔒 Bill #{r.Locked_Bill_ID ?? '?'}
                        </span>
                      )}
                      {verifyOn && (r.VerifiedOn ? (
                        <span className="block w-fit rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800"
                          title={`Verified ${new Date(r.VerifiedOn).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}>
                          ✓ Verified{r.VerifiedByName ? ` · ${r.VerifiedByName}` : ''}
                        </span>
                      ) : (
                        <span className="block w-fit rounded bg-yellow-100 px-1.5 py-0.5 font-semibold text-yellow-800">To verify</span>
                      ))}
                      {r.QCStatus && QC_CHIP[qcChipKey(r)] && (
                        <span className={`block w-fit rounded px-1.5 py-0.5 font-semibold ${QC_CHIP[qcChipKey(r)]?.cls ?? ''}`}
                          title={r.QCStatus === 'QC_REWORK' && r.QCReason ? r.QCReason : undefined}>
                          {QC_CHIP[qcChipKey(r)]?.text}
                          {r.QCStatus === 'QC_REWORK' && r.ReworkQty ? ` · ${Number(r.ReworkQty).toLocaleString('en-IN')}` : ''}
                        </span>
                      )}
                      {r.QCStatus === 'QC_REWORK' && canAdd && (
                        <button className="block text-[11px] font-semibold text-sky-700 hover:underline" onClick={() => startResorted(r)}
                          title="The contractor has sorted them again: enter the Resorting work, which goes back to Quality">
                          Resorting done →
                        </button>
                      )}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-3 py-2 text-right text-xs text-slate-600">
                        {r.Rate == null ? '—' : inr(r.Rate, 2)}
                      </td>
                    )}
                    {isSuperAdmin && <td className="px-3 py-2 text-right text-xs font-medium">{rupees(r.Amount)}</td>}
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {ed ? <input className={`${cell} min-w-[140px]`} value={ed.remarks} maxLength={500} onChange={setEd('remarks')} /> : r.Remarks}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.CreatedByName}</td>
                    {showActions && (
                      <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                        {verifyOn && canVerify && (r.VerifiedOn ? (
                          <button className="text-xs text-slate-500 hover:underline" disabled={verifyMut.isPending}
                            onClick={() => { if (confirm('Take the Verified mark off this entry?')) verifyMut.mutate({ ids: [r.ProductionLogID], verified: false }); }}>Unverify</button>
                        ) : (
                          <button className="text-xs font-semibold text-emerald-700 hover:underline" disabled={verifyMut.isPending}
                            onClick={() => verifyMut.mutate({ ids: [r.ProductionLogID], verified: true })}>Verify</button>
                        ))}
                        {isLocked(r) ? (
                          canCorrect ? (
                            <>
                              <button className="text-xs text-amber-700 hover:underline" onClick={() => startCorrect(r)}>Correct</button>
                              <button className="text-xs text-slate-600 hover:underline" onClick={() => setAuditId(r.ProductionLogID)}>History</button>
                            </>
                          ) : <span className="text-xs text-slate-400" title="On a finalized contractor bill">Locked</span>
                        ) : canEdit && ed ? (
                          <>
                            <button className="text-xs font-semibold text-emerald-700 hover:underline"
                              disabled={inlineMut.isPending || !ed.operationId || !(Number(ed.qty) > 0) || !ed.date
                                || (edOp?.kind === 'BOX' && !ed.pasted && !stepOps.some((o) => !ed.skipped.includes(o.id)))}
                              onClick={() => inlineMut.mutate({ row: r, ed })}>
                              {inlineMut.isPending ? 'Saving…' : 'Save'}
                            </button>
                            <button className="text-xs text-slate-500 hover:underline" disabled={inlineMut.isPending}
                              onClick={() => { setInline(null); setInlineErr(''); }}>Cancel</button>
                            {inlineErr && <div className="mt-1 max-w-[220px] whitespace-normal text-left text-[11px] text-red-600">{inlineErr}</div>}
                          </>
                        ) : canEdit ? (
                          <>
                            <button className="text-xs text-brand hover:underline" onClick={() => startInline(r)}
                              title="Edit in this row">Edit</button>
                            <button
                              className="text-xs text-red-600 hover:underline"
                              onClick={() => { if (confirm(`Delete production entry for Job ${r.Job_ID}?`)) deleteMut.mutate(r.ProductionLogID); }}
                            >
                              Delete
                            </button>
                          </>
                        ) : null}
                      </td>
                    )}
                  </tr>
                  );
                })}
                {list.data.rows.length === 0 && (
                  <tr><td colSpan={12 + (isSuperAdmin ? 2 : 0) + (showActions ? 1 : 0)} className="px-3 py-8 text-center text-slate-400">No entries found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 pt-2">
              <button className="btn-ghost text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
              <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
              <button className="btn-ghost text-xs" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}

      {auditId != null && <CorrectionHistory id={auditId} onClose={() => setAuditId(null)} />}
    </div>
  );
}

/** A group of table rows under one key. */
function FragmentRows({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/** The audit trail of a locked entry: every corrected field, old → new, who, when, why. */
function CorrectionHistory({ id, onClose }: { id: number; onClose: () => void }) {
  const audit = useQuery({
    queryKey: ['production-audit', id],
    queryFn: () => api<{ rows: AuditRow[] }>(`/api/production/${id}/audit`),
  });
  const rows = audit.data?.rows ?? [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-3xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="card-header flex items-center justify-between">
          <span>Correction history — entry #{id}</span>
          <button className="text-white/80 hover:text-white text-lg leading-none" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="card-body">
          {audit.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {audit.isError && <p className="text-sm text-red-600">{friendlyProdError((audit.error as Error).message)}</p>}
          {audit.data && rows.length === 0 && <p className="text-sm text-slate-500">No corrections recorded for this entry.</p>}
          {rows.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-1.5">When</th>
                  <th className="px-2 py-1.5">By</th>
                  <th className="px-2 py-1.5">Field</th>
                  <th className="px-2 py-1.5">Old → New</th>
                  <th className="px-2 py-1.5">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 align-top">
                    <td className="px-2 py-1 whitespace-nowrap">
                      {new Date(r.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-2 py-1">{r.by ?? '—'}</td>
                    <td className="px-2 py-1 font-medium">{r.field ?? r.code}</td>
                    <td className="px-2 py-1">
                      <span className="text-red-700 line-through decoration-red-300">{r.old ?? '—'}</span>
                      {' → '}
                      <span className="text-emerald-700">{r.new ?? '—'}</span>
                    </td>
                    <td className="px-2 py-1 text-slate-600">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
